import * as React from 'react';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import { isEmpty } from 'lodash';
import { FieldBase } from 'view/common/fields/field-base';
import AkTextfield from '@atlaskit/textfield/text-field';
import { withControlledFormComponentValue } from '../../with-controlled-form-component-value';

export interface Props extends FieldFormProps<string> {
	defaultValue?: string;
	value?: string;
	shouldFitContainer: boolean;
	stateless?: boolean;
	/** Used when we want to wrap something around the FieldText, for example onboarding spotlights. */
	render?: (field: React.ReactNode) => React.ReactNode;
	placeholder?: string;
	isLabelHidden?: boolean;
	isInvalid?: boolean;
	type?: string;
	testId?: string;
	maxLength?: number;
	onFocus?: () => void;
	onBlur?: () => void;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	isValidationHidden?: boolean;
	autoFocus?: boolean;
	ariaDescribedby?: string;
	autoComplete?: string;
	ariaLabelledby?: string;
	tooltip?: React.ReactNode;
}

export default class TextField extends React.PureComponent<Props> {
	static defaultProps = {
		shouldFitContainer: true,
		type: 'text',
	};

	inputRef: React.RefObject<HTMLInputElement> = React.createRef();

	//TODO - We need to find a better way to add focus to input field when there is an error. This should be done in the form component. https://hello.jira.atlassian.cloud/browse/CHANNELSUI-258

	onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		if (this.props.onChange) {
			this.props.onChange(this.props.id, e.target.value, false, {
				rawObject: e,
				rawValue: e.target.value,
			});
		}
	};

	onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
		if (this.props.onFieldBlurredByUser) {
			this.props.onFieldBlurredByUser({
				field: this.props.id,
				value: e.target.value,
				additionalFieldData: {
					rawObject: e,
					rawValue: e.target.value,
				},
			});
		}

		if (this.props.onBlur) {
			this.props.onBlur();
		}
	};

	render() {
		const {
			id,
			testId,
			label,
			required,
			defaultValue,
			description,
			errorMessage,
			maxLength,
			disabled,
			render,
			placeholder,
			isLabelHidden,
			type,
			autoFocus,
			value,
			onFocus,
			onKeyDown,
			ariaDescribedby,
			autoComplete,
			ariaLabelledby,
			tooltip,
			isInvalid: isInvalidProp,
		} = this.props;
		const isInvalid = isInvalidProp || !isEmpty(errorMessage);
		const inputType = type;
		/** Currently to match the legacy behavior in some cases we are passing both value and defaultValue
		 * there will be a sentry alert to either use controlled or uncontrolled but not both
		 * to prevent this we only pass one of value or defaultValue
		 * such scenarios can be avoided once we clean the legacy code
		 * when cleaning up feature flag see if this can be removed
		 */
		const isValueDefined = typeof value === 'string';
		const textFieldValueProps = isValueDefined
			? {
					value,
				}
			: {
					defaultValue,
				};
		const ariaDescribedByText =
			ariaDescribedby || `${id}-field-description ${isInvalid ? 'field-error' : ''}`;
		const ariaLabelledByText = ariaLabelledby || `${id}-label`;
		const field = (
			<AkTextfield
				id={id}
				testId={testId}
				name={id}
				aria-describedby={ariaDescribedByText}
				aria-labelledby={ariaLabelledByText}
				aria-invalid={isInvalid}
				aria-required={required}
				{...textFieldValueProps}
				onChange={this.onChange}
				isInvalid={isInvalid}
				isDisabled={disabled}
				placeholder={placeholder}
				type={inputType}
				maxLength={maxLength}
				autoFocus={autoFocus}
				onFocus={onFocus}
				onBlur={this.onBlur}
				onKeyDown={onKeyDown}
				autoComplete={autoComplete}
			/>
		);

		return (
			<FieldBase
				htmlFor={id}
				label={label}
				required={required}
				description={description}
				errorMessage={errorMessage}
				noLabel={isLabelHidden}
				labelId={`${id}-label`}
				{...(isUiModificationsSupportEnabled() ? { tooltip } : null)}
			>
				{render ? render(field) : field}
			</FieldBase>
		);
	}
}

export const ControlledTextField = withControlledFormComponentValue(
	(_field: string, value: string) => value,
)(TextField);
